// /stores/auth.js
import { defineStore } from "pinia";
import authService from "@/api/authService";
import { jwtDecode } from "jwt-decode";
import { useUserStore } from "./user";
import router from "@/router";

export function isTokenExpired(token, bufferTimeInSeconds = 60) {
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    const currentTime = Date.now();
    const expirationTime = exp * 1000;

    if (expirationTime - currentTime < bufferTimeInSeconds * 1000) {
      return true;
    }
  } catch (error) {
    console.error("Failed to decode token:", error);
    return true;
  }
  return false;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    accessToken: localStorage.getItem("access_token") || null,
    refreshToken: localStorage.getItem("refresh_token") || null,
    isAuthenticated: !!localStorage.getItem("access_token"),
    verificationAndAuthorizationValuesExist:
      localStorage.getItem("is_gmail_authorized") !== null &&
      localStorage.getItem("is_email_verified") !== null &&
      localStorage.getItem("is_phone_verified") !== null,
    isGmailAuthorized: localStorage.getItem("is_gmail_authorized") === "true",
    isOutlookAuthorized:
      localStorage.getItem("is_outlook_authorized") === "true",
    isEmailAuthorized: localStorage.getItem("is_email_authorized") === "true",
    isEmailVerified: localStorage.getItem("is_email_verified") === "true",
    isPhoneVerified: localStorage.getItem("is_phone_verified") === "true",
    phoneNumber: null,
    isPhoneVerificationTriggered: false,
    phoneCountdown: 60,
    isPhoneCodeSent: false,
    isPhoneVerificationSuccess: null,
    email: null,
    isEmailVerificationTriggered: false,
    emailCountdown: 60,
    isEmailCodeSent: false,
    isEmailVerificationSuccess: null,
  }),
  actions: {
    async login(credentials) {
      const userStore = useUserStore();
      try {
        const { data } = await authService.login(credentials);
        this.accessToken = data.access_token;
        this.refreshToken = data.refresh_token;
        this.isAuthenticated = true;
        this.isGmailAuthorized = data.is_gmail_authorized;
        this.isOutlookAuthorized = data.is_outlook_authorized;
        this.isEmailAuthorized = data.is_email_authorized;
        this.isEmailVerified = data.is_email_verified;
        this.isPhoneVerified = data.is_phone_verified;
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("is_gmail_authorized", data.is_gmail_authorized);
        localStorage.setItem(
          "is_outlook_authorized",
          data.is_outlook_authorized
        );
        localStorage.setItem("is_email_authorized", data.is_email_authorized);
        localStorage.setItem("is_email_verified", data.is_email_verified);
        localStorage.setItem("is_phone_verified", data.is_phone_verified);

        await userStore.fetchUserProfile();

        router.push({ name: "ProjectsPage" });

        /*if (!data.is_email_verified) {
          try {
            await userStore.fetchUserProfile();
            this.email = userStore.profile.email;

            // Redirect to email verification page
            router.push({ name: 'VerifyEmail' });

            // Trigger the initial email verification code
            await this.requestEmailVerificationCode(this.email);
            
          } catch (userError) {
            console.error('Failed to fetch user details:', userError);
            throw userError;
          }
        } else if (!data.is_phone_verified) {
          try {
            await userStore.fetchUserProfile();
            this.phoneNumber = userStore.profile.phone_number;

            // Redirect to phone verification page
            router.push({ name: 'VerifyPhone' });

            // Trigger the initial SMS code
            await this.requestSmsVerificationCode(this.phoneNumber);
            
          } catch (userError) {
            console.error('Failed to fetch user details:', userError);
            throw userError;
          }
        } else {
          router.push({ name: "ProjectsPage" });
        }*/
      } catch (error) {
        console.error("Login error:", error);
        throw error;
      }
    },
    async register(credentials) {
      const { data } = await authService.register(credentials);
      return data;
    },

    initializeAuthState() {
      const token = localStorage.getItem("access_token");
      if (token && !isTokenExpired(token)) {
        this.accessToken = token;
        this.refreshToken = localStorage.getItem("refresh_token");
        this.isAuthenticated = true;
        this.isGmailAuthorized =
          localStorage.getItem("is_gmail_authorized") === "true";
        this.isOutlookAuthorized =
          localStorage.getItem("is_outlook_authorized") === "true";
        this.isEmailAuthorized =
          localStorage.getItem("is_email_authorized") === "true";
        this.isEmailVerified =
          localStorage.getItem("is_email_verified") === "true";
        this.isPhoneVerified =
          localStorage.getItem("is_phone_verified") === "true";
      } else {
        this.refreshTokenIfNeeded();
      }
    },

    setGmailAuthorized() {
      this.isGmailAuthorized = true;
      localStorage.setItem("is_gmail_authorized", "true");
    },

    setOutlookAuthorized() {
      this.isOutlookAuthorized = true;
      localStorage.setItem("is_outlook_authorized", "true");
    },

    async refreshTokenIfNeeded() {
      if (isTokenExpired(this.accessToken, 60)) {
        try {
          const { data } = await this.refreshAccessToken();
          localStorage.setItem("access_token", data.access_token);
          localStorage.setItem("refresh_token", data.refresh_token);
        } catch (error) {
          this.logout();
        }
      }
    },
    async refreshAccessToken() {
      try {
        const { data } = await authService.refreshToken(this.refreshToken);
        this.accessToken = data.access_token;
        this.refreshToken = data.refresh_token;
        this.isAuthenticated = true;
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        return data.access_token;
      } catch (error) {
        this.logout();
        throw error;
      }
    },
    logout() {
      const userStore = useUserStore();
      this.accessToken = null;
      this.refreshToken = null;
      this.isAuthenticated = false;
      this.isEmailVerified = false;
      this.isPhoneVerified = false;
      this.isGmailAuthorized = false;
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("is_gmail_authorized");
      localStorage.removeItem("is_outlook_authorized");
      localStorage.removeItem("is_email_authorized");
      localStorage.removeItem("is_email_verified");
      localStorage.removeItem("is_phone_verified");

      userStore.$reset();
    },
    async requestSmsVerificationCode(phone_number) {
      try {
        this.startPhoneCountdown();
        await authService.requestSmsVerificationCode(phone_number);
        this.isPhoneCodeSent = true;
        this.isPhoneVerificationTriggered = true;
      } catch (error) {
        console.error("SMS verification code request error:", error);
        this.isPhoneCodeSent = false;
        throw error;
      }
    },
    async verifyPhoneNumber(phone_number, code) {
      try {
        await authService.verifyPhoneNumber(phone_number, code);
        this.isPhoneVerificationSuccess = true;
        this.isPhoneVerificationTriggered = false;
        localStorage.setItem("is_phone_verified", "true");
        this.isPhoneVerified = true;
      } catch (error) {
        this.isPhoneVerificationSuccess = false;
        console.error("Phone number verification error:", error);
        throw error;
      }
    },
    async requestEmailVerificationCode(email) {
      try {
        this.startEmailCountdown();
        await authService.requestEmailVerificationCode(email);
        this.isEmailCodeSent = true;
        this.isEmailVerificationTriggered = true;
      } catch (error) {
        console.error("Email verification code request error:", error);
        this.isEmailCodeSent = false;
        throw error;
      }
    },

    async verifyEmail(email, code) {
      try {
        await authService.verifyEmail(email, code);
        this.isEmailVerificationSuccess = true;
        this.isEmailVerificationTriggered = false;
        localStorage.setItem("is_email_verified", "true");
        this.isEmailVerified = true;

        if (!this.isPhoneVerified) {
          router.push({ name: "VerifyPhone" });
        } else {
          router.push({ name: "ProjectsPage" });
        }
        // You might want to update user state or redirect after successful verification
      } catch (error) {
        this.isEmailVerificationSuccess = false;
        console.error("Email verification error:", error);
        throw error;
      }
    },

    startEmailCountdown() {
      this.emailCountdown = 60;
      const timer = setInterval(() => {
        if (this.emailCountdown > 0) {
          this.emailCountdown--;
        } else {
          clearInterval(timer);
        }
      }, 1000);
    },

    canResendEmailCode() {
      return this.emailCountdown === 0;
    },
    startPhoneCountdown() {
      this.phoneCountdown = 60;
      const timer = setInterval(() => {
        if (this.phoneCountdown > 0) {
          this.phoneCountdown--;
        } else {
          clearInterval(timer);
        }
      }, 1000);
    },
    canResendPhoneCode() {
      return this.phoneCountdown === 0;
    },
  },
});
